import React, { useEffect, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Wallet,
  Settings,
  Star,
  UserCircle,
  ChevronDown,
  ChevronRight,
  Bell,
  Search,
  Plus,
  TrendingUp,
  Eye,
  Edit,
  Trash2,
  MoreHorizontal,
  ArrowUpRight,
  ArrowDownRight,
  Zap,
  BarChart3,
  FileText,
  CreditCard,
  MessageCircle,
  Home,
  LogOut,
} from "lucide-react";
import * as signalR from "@microsoft/signalr";
import { motion, AnimatePresence } from "framer-motion";

const SellerDashboard = () => {
  const location = useLocation();
  const [openMenu, setOpenMenu] = useState(null);
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([
    { id: 1, message: "New order #1234 received", time: "2 min ago", type: "order" },
    { id: 2, message: "Product stock running low", time: "15 min ago", type: "alert" },
  ]);
  const [showNotifications, setShowNotifications] = useState(false);

  // SignalR connection
  useEffect(() => {
    const connection = new signalR.HubConnectionBuilder()
      .withUrl("https://localhost:44370/notificationHub")
      .withAutomaticReconnect()
      .build();

    connection.start()
      .then(() => console.log("SignalR connected"))
      .catch(err => console.error("SignalR connection error:", err));

    connection.on("ReceiveNotification", (message) => {
      const newNotification = {
        id: Date.now(),
        message,
        time: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        type: "order"
      };
      setNotifications(prev => [newNotification, ...prev]);
    });

    return () => connection.stop();
  }, []);

  const toggleMenu = (name) => {
    setOpenMenu(openMenu === name ? null : name);
  };

  // Stats data
  const stats = [
    { name: "Total Revenue", value: "₹1,25,000", change: "+12.5%", positive: true, icon: <TrendingUp size={16} /> },
    { name: "Products", value: "32", icon: <Package size={16} /> },
    { name: "Orders", value: "156", change: "+8", positive: true, icon: <ShoppingCart size={16} /> },
    { name: "Rating", value: "4.8★", icon: <Star size={16} /> },
  ];

  // Quick actions
  const quickActions = [
    { icon: <Plus size={18} />, label: "Add Product", path: "/____seller_dashboard/addProducts", color: "bg-blue-500" },
    { icon: <BarChart3 size={18} />, label: "View Reports", path: "/____seller_dashboard/reports", color: "bg-purple-500" },
    { icon: <MessageCircle size={18} />, label: "Messages", path: "/____seller_dashboard/customersQueries", color: "bg-green-500" },
    { icon: <FileText size={18} />, label: "Invoices", path: "/____seller_dashboard/invoice", color: "bg-orange-500" },
  ];

  // Menu structure
  const menu = [
    {
      name: "Dashboard",
      icon: <Home size={18} />,
      path: "/____seller_dashboard",
    },
    {
      name: "Products",
      icon: <Package size={18} />,
      children: [
        { name: "All Products", path: "/____seller_dashboard/products" },
        { name: "Add Product", path: "/____seller_dashboard/addProducts" },
        { name: "Manage Inventory", path: "/____seller_dashboard/manage_inventory" },
        { name: "Categories", path: "/____seller_dashboard/categories" },
      ],
    },
    {
      name: "Orders",
      icon: <ShoppingCart size={18} />,
      children: [
        { name: "Order Dashboard", path: "/____seller_dashboard/orderdashboard" },
        { name: "New Orders", path: "/____seller_dashboard/neworders" },
        { name: "Delivered Orders", path: "/____seller_dashboard/deleveredOrders" },
        { name: "Returns", path: "/____seller_dashboard/returnOrders" },
      ],
    },
    {
      name: "Transactions",
      icon: <Wallet size={18} />,
      children: [
        { name: "Earnings", path: "/____seller_dashboard/earning" },
        { name: "Invoices", path: "/____seller_dashboard/invoice" },
      ],
    },
    {
      name: "Reviews",
      icon: <Star size={18} />,
      children: [
        { name: "Customer Reviews", path: "/seller/reviews/customers" },
      ],
    },
    {
      name: "Settings",
      icon: <Settings size={18} />,
      children: [
        { name: "Store Profile", path: "/seller/settings/profile" },
        { name: "Profile Settings", path: "/____seller_dashboard/profileSettings" },
        { name: "Logout", path: "/____seller_dashboard/sellerlogout" },
      ],
    },
  ];

  // Recent orders data
  const recentOrders = [
    { id: "#ORD-1234", customer: "John Doe", amount: "₹2,500", status: "Delivered", date: "2 min ago" },
    { id: "#ORD-1235", customer: "Jane Smith", amount: "₹1,800", status: "Processing", date: "15 min ago" },
    { id: "#ORD-1236", customer: "Mike Johnson", amount: "₹3,200", status: "Pending", date: "1 hour ago" },
    { id: "#ORD-1237", customer: "Sarah Wilson", amount: "₹950", status: "Delivered", date: "2 hours ago" },
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case "Delivered": return "bg-green-100 text-green-700";
      case "Processing": return "bg-blue-100 text-blue-700";
      case "Pending": return "bg-yellow-100 text-yellow-700";
      default: return "bg-gray-100 text-gray-700";
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* LEFT SIDEBAR */}
      <motion.aside
        initial={{ x: -100, opacity: 0 }}
        animate={{ x: 0, opacity: 1 }}
        className="w-64 bg-white shadow-xl flex flex-col border-r border-gray-100"
      >
        {/* Logo */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-500/30">
              <Zap className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">Seller Hub</h2>
              <p className="text-xs text-gray-400">Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 overflow-y-auto">
          {menu.map((item) => {
            const isActive = location.pathname === item.path;

            if (!item.children) {
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  className={`flex items-center gap-3 p-3 rounded-xl mb-1 transition-all duration-200 ${
                    isActive
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <span className="w-5 h-5">{item.icon}</span>
                  <span className="font-medium">{item.name}</span>
                </Link>
              );
            }

            const isOpen = openMenu === item.name;

            return (
              <div key={item.name} className="mb-1">
                <button
                  onClick={() => toggleMenu(item.name)}
                  className={`w-full flex items-center justify-between p-3 rounded-xl transition-all duration-200 ${
                    isOpen ? "bg-gray-100 text-gray-800" : "text-gray-600 hover:bg-gray-100"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="w-5 h-5">{item.icon}</span>
                    <span className="font-medium">{item.name}</span>
                  </div>
                  <motion.div
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <ChevronDown size={16} />
                  </motion.div>
                </button>

                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="ml-4 mt-1 space-y-1 overflow-hidden"
                    >
                      {item.children.map((child) => {
                        const isActiveChild = location.pathname === child.path;
                        return (
                          <Link
                            key={child.name}
                            to={child.path}
                            className={`block p-2.5 rounded-lg text-sm transition-all ${
                              isActiveChild
                                ? "bg-blue-50 text-blue-600 font-medium"
                                : "text-gray-500 hover:text-gray-700 hover:bg-gray-50"
                            }`}
                          >
                            {child.name}
                          </Link>
                        );
                      })}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </nav>

        {/* User Profile */}
        <div className="p-4 border-t border-gray-100">
          <div 
            onClick={() => navigate("/____seller_dashboard/profileSettings")}
            className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 hover:bg-gray-100 cursor-pointer transition-all"
          >
            <img
              src="https://i.pravatar.cc/40"
              alt="Profile"
              className="w-10 h-10 rounded-xl object-cover shadow-md"
            />
            <div className="flex-1">
              <h4 className="font-semibold text-gray-800 text-sm">Seller</h4>
              <p className="text-xs text-green-500 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                Online
              </p>
            </div>
          </div>
        </div>
      </motion.aside>

      {/* RIGHT CONTENT AREA */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* TOP NAVBAR */}
        <motion.header
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="bg-white shadow-sm border-b border-gray-100 px-6 py-4 sticky top-0 z-40"
        >
          <div className="flex items-center justify-between gap-6">
            {/* Search Bar */}
            <div className="flex items-center gap-3 bg-gray-100 px-4 py-2.5 rounded-xl w-96">
              <Search size={18} className="text-gray-400" />
              <input
                type="text"
                placeholder="Search orders, products..."
                className="bg-transparent outline-none w-full text-gray-700 placeholder-gray-400"
              />
            </div>

            {/* Quick Stats */}
            <div className="flex items-center gap-4">
              {stats.map((stat, index) => (
                <motion.button
                  key={stat.name}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => stat.path && navigate(stat.path)}
                  className="flex items-center gap-2 bg-gradient-to-br from-gray-50 to-gray-100 px-4 py-2 rounded-xl hover:shadow-md transition-all border border-gray-200"
                >
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    stat.positive ? "bg-green-100 text-green-600" : "bg-indigo-100 text-indigo-600"
                  }`}>
                    {stat.icon}
                  </div>
                  <div className="text-left">
                    <p className="text-xs text-gray-500">{stat.name}</p>
                    <div className="flex items-center gap-1">
                      <p className="text-sm font-bold text-gray-800">{stat.value}</p>
                      {stat.change && (
                        <span className={`text-xs flex items-center ${
                          stat.positive ? "text-green-600" : "text-red-600"
                        }`}>
                          {stat.positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                          {stat.change}
                        </span>
                      )}
                    </div>
                  </div>
                </motion.button>
              ))}
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-4">
              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-all"
                >
                  <Bell size={20} className="text-gray-600" />
                  {notifications.length > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-red-500 rounded-full text-[10px] text-white flex items-center justify-center font-medium">
                      {notifications.length}
                    </span>
                  )}
                </button>

                <AnimatePresence>
                  {showNotifications && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-80 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50"
                    >
                      <div className="p-4 border-b border-gray-100 flex items-center justify-between">
                        <h4 className="font-semibold text-gray-800">Notifications</h4>
                        <button className="text-xs text-blue-600 hover:text-blue-700">Mark all read</button>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                          <p className="p-4 text-gray-500 text-sm text-center">No new notifications</p>
                        ) : (
                          notifications.map((n) => (
                            <div key={n.id} className="p-4 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors">
                              <div className="flex items-start gap-3">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                                  n.type === "order" ? "bg-blue-100 text-blue-600" : "bg-red-100 text-red-600"
                                }`}>
                                  {n.type === "order" ? <ShoppingCart size={14} /> : <Bell size={14} />}
                                </div>
                                <div className="flex-1">
                                  <p className="text-sm text-gray-700">{n.message}</p>
                                  <p className="text-xs text-gray-400 mt-1">{n.time}</p>
                                </div>
                              </div>
                            </div>
                          ))
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Quick Actions Dropdown */}
              <div className="relative">
                <button
                  onClick={() => toggleMenu("quickActions")}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-blue-500/30 transition-all"
                >
                  <Plus size={18} />
                  <span className="font-medium">Quick Actions</span>
                </button>

                <AnimatePresence>
                  {openMenu === "quickActions" && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      className="absolute right-0 mt-2 w-56 bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden z-50 py-2"
                    >
                      {quickActions.map((action, index) => (
                        <button
                          key={action.label}
                          onClick={() => { navigate(action.path); setOpenMenu(null); }}
                          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 transition-colors"
                        >
                          <div className={`w-9 h-9 rounded-lg ${action.color} flex items-center justify-center text-white`}>
                            {action.icon}
                          </div>
                          <span className="font-medium text-gray-700">{action.label}</span>
                        </button>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </motion.header>

        {/* MAIN CONTENT */}
        <main className="flex-1 overflow-y-auto p-6 bg-gray-50">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-7xl mx-auto"
          >
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl font-bold text-gray-800">Welcome back! 👋</h1>
              <p className="text-gray-500 mt-1">Here's what's happening with your store today.</p>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={stat.name}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-300"
                >
                  <div className="flex items-center justify-between mb-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      stat.positive ? "bg-green-100 text-green-600" : "bg-indigo-100 text-indigo-600"
                    }`}>
                      {stat.icon}
                    </div>
                    {stat.change && (
                      <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        stat.positive ? "bg-green-100 text-green-600" : "bg-red-100 text-red-600"
                      }`}>
                        {stat.positive ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                        {stat.change}
                      </span>
                    )}
                  </div>
                  <h3 className="text-gray-500 text-sm font-medium">{stat.name}</h3>
                  <p className="text-3xl font-bold text-gray-800 mt-1">{stat.value}</p>
                </motion.div>
              ))}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {quickActions.map((action, index) => (
                <motion.button
                  key={action.label}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => navigate(action.path)}
                  className="flex items-center gap-3 bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-lg hover:border-gray-200 transition-all group"
                >
                  <div className={`w-10 h-10 rounded-lg ${action.color} flex items-center justify-center text-white group-hover:scale-110 transition-transform`}>
                    {action.icon}
                  </div>
                  <span className="font-medium text-gray-700">{action.label}</span>
                </motion.button>
              ))}
            </div>

            {/* Recent Orders Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-8"
            >
              <div className="p-6 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-gray-800">Recent Orders</h3>
                  <p className="text-sm text-gray-500">Latest orders from your customers</p>
                </div>
                <button
                  onClick={() => navigate("/____seller_dashboard/orderdashboard")}
                  className="text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  View All <ArrowUpRight size={16} />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Order ID</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                      <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {recentOrders.map((order) => (
                      <tr key={order.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="font-medium text-gray-800">{order.id}</span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center text-white text-sm font-medium">
                              {order.customer.charAt(0)}
                            </div>
                            <span className="text-gray-700">{order.customer}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-800">{order.amount}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                            {order.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-gray-500">{order.date}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            <button className="p-2 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
                              <Eye size={16} />
                            </button>
                            <button className="p-2 rounded-lg hover:bg-blue-50 text-gray-400 hover:text-blue-600 transition-colors">
                              <Edit size={16} />
                            </button>
                            <button className="p-2 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-600 transition-colors">
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* PAGE CONTENT */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-2xl shadow-sm border border-gray-100"
            >
              <Outlet />
            </motion.div>
          </motion.div>
        </main>
      </div>
    </div>
  );
};

export default SellerDashboard;
